/**
 * BoutiqueLoader — single source of truth for "loading" states across the
 * customer storefront. Aesthetic matches the boutique theme (rosegold +
 * ivory + wine), and supports two visual variants:
 *
 *   • <BoutiqueLoader />              → page-fill (vertical centred)
 *   • <BoutiqueLoader compact />      → inline 80px centred
 *
 * The motif: a slowly rotating rosegold ring with a wine inner pulse,
 * paired with a tasteful caption. Uses pure Tailwind + an inline @keyframes
 * style block so it works everywhere with no external libraries and no
 * theme-config edits required.
 *
 * Optional caption — keep it short, on-brand. Defaults to a friendly,
 * non-tense word so a slow connection feels intentional, not anxious.
 */
export default function BoutiqueLoader({
    label,
    sublabel,
    compact = false,
    fullScreen = false,
    className = "",
    "data-testid": testid = "boutique-loader",
}) {
    const ringSize = compact ? 56 : 80;
    const inner = compact ? 32 : 50;
    return (
        <div
            data-testid={testid}
            className={`flex flex-col items-center justify-center text-center
                ${fullScreen ? "fixed inset-0 z-[200] bg-ivory/90 backdrop-blur-sm" : ""}
                ${compact ? "py-6" : "py-16"} ${className}`}
            role="status"
            aria-live="polite"
        >
            <div className="relative" style={{ width: ringSize, height: ringSize }}>
                {/* Rotating rosegold ring — segmented gradient, slow spin */}
                <span
                    className="absolute inset-0 rounded-full bouroller-ring"
                    style={{
                        background: "conic-gradient(from 0deg, rgba(201,164,122,0) 0deg, rgba(201,164,122,0.35) 90deg, rgba(140,55,75,0.85) 200deg, rgba(201,164,122,0) 360deg)",
                    }}
                />
                {/* Mask the centre to leave just a 3px ring */}
                <span
                    className="absolute rounded-full bg-ivory"
                    style={{
                        inset: 3,
                    }}
                />
                {/* Inner wine pulse + sparkle dot */}
                <span
                    className="absolute rounded-full bouroller-pulse"
                    style={{
                        width: inner,
                        height: inner,
                        top: (ringSize - inner) / 2,
                        left: (ringSize - inner) / 2,
                        background: "radial-gradient(circle at 30% 30%, rgba(201,164,122,0.65) 0%, rgba(140,55,75,0.4) 60%, rgba(140,55,75,0) 100%)",
                    }}
                />
                <span
                    className="absolute w-1.5 h-1.5 rounded-full bg-rosegold bouroller-orbit"
                    style={{
                        top: 2,
                        left: ringSize / 2 - 3,
                    }}
                />
            </div>
            {(label || !compact) && (
                <p
                    className={`mt-${compact ? 3 : 4} font-display ${compact ? "text-sm" : "text-base"} text-ink font-light tracking-wide`}
                >
                    {label || "Curating just for you…"}
                </p>
            )}
            {sublabel && (
                <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-rosegold font-bold">
                    {sublabel}
                </p>
            )}
            {/* Local keyframes — namespaced so we don't collide with any
                third-party `loader` / `pulse` classes on the page. */}
            <style>{`
                @keyframes bouroller-spin { to { transform: rotate(360deg); } }
                @keyframes bouroller-orbit-spin { to { transform: rotate(360deg); } }
                @keyframes bouroller-pulse-anim {
                    0%, 100% { opacity: 0.65; transform: scale(0.92); }
                    50%      { opacity: 1;    transform: scale(1.04); }
                }
                .bouroller-ring  { animation: bouroller-spin 2.6s linear infinite; }
                .bouroller-pulse { animation: bouroller-pulse-anim 1.6s ease-in-out infinite; }
                .bouroller-orbit {
                    transform-origin: 4px ${ringSize / 2 - 1}px;
                    animation: bouroller-orbit-spin 1.8s linear infinite;
                }
                @media (prefers-reduced-motion: reduce) {
                    .bouroller-ring, .bouroller-orbit { animation-duration: 6s; }
                    .bouroller-pulse { animation: none; opacity: 1; }
                }
            `}</style>
            <span className="sr-only">Loading…</span>
        </div>
    );
}
